/**
 * DischargeHistoryTable Component
 * Paginated discharge history table with row-click detail panel.
 * Fetches history when the isActive prop becomes true and provides
 * pagination, loading states, and an empty-state fallback.
 *
 * Enhanced with:
 * - Per-power proof status (Proof Required / Proof Uploaded)
 * - Proof upload buttons opening ProofUploadModal
 * - "Complete Discharge" action for reports with pending proof
 * - Discharged/total progress in history rows
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  ChevronRightIcon,
  ArrowUpTrayIcon,
  DocumentCheckIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { OathButton, OathSpinner, OathAlert, OathEmptyState, OathStatusBadge, OathSearchInput } from '../../../shared/OathUI';
import { OathPagination } from '../../../shared/OathTableComponents';
import { formatCurrencyWithSymbol, formatDate } from '../utils/formatters';
import { HISTORY_STATUS_FILTERS } from '../utils/constants';
import { getHistory, getStatus, completeDischarge, removePowerFromReport } from '../../../../services/oathDischargeService';
import { StatusFilterPills } from './shared';
import ProofUploadModal from '../modals/ProofUploadModal';

const DEFAULT_PAGE_SIZE = 20;

// ============================================================================
// Detail panel shown when a row is selected
// ============================================================================

const DetailPanel = ({
  detail,
  loading,
  error,
  onClose,
  onUploadProof,
  onCompleteDischarge,
  completing,
  onRemovePower,
  removingPower,
}) => {
  if (loading) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/80 border-t border-b border-primary/20 dark:border-primary/10">
        <div className="h-0.5 bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40" />
        <div className="flex items-center justify-center py-12">
          <OathSpinner size="md" className="text-primary" />
          <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
            Loading report details...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800/80 border-t border-b border-red-200 dark:border-red-900/30">
        <div className="h-0.5 bg-gradient-to-r from-red-400/40 via-red-500/60 to-red-400/40" />
        <div className="p-5 qhd:p-6 4k:p-8">
          <OathAlert variant="error" message={error} />
          <div className="mt-4 text-center">
            <OathButton variant="outline" size="sm" onClick={onClose}>
              Dismiss
            </OathButton>
          </div>
        </div>
      </div>
    );
  }

  if (!detail) return null;

  const {
    reportNumber,
    status,
    submittedDate,
    powers = [],
    reviewedBy,
    reviewedDate,
    totalLiability,
    errorMessage,
  } = detail;

  // Determine actionable state of pending powers
  const pendingPowers = powers.filter((p) => p.isPending);
  const pendingWithProof = pendingPowers.filter((p) => p.hasProofUploaded);
  const pendingWithoutProof = pendingPowers.filter((p) => !p.hasProofUploaded);
  // Show "Complete Discharge" only when some pending powers still need proof uploaded
  // If all pending powers have proof, they're already in the review pipeline
  const showCompleteAction = pendingWithoutProof.length > 0 && pendingWithProof.length > 0;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/80 border-t border-b border-primary/20 dark:border-primary/10">
      {/* Accent bar */}
      <div className="h-0.5 bg-gradient-to-r from-primary/40 via-primary/60 to-primary/40" />

      <div className="p-5 qhd:p-6 4k:p-8 space-y-5">
        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400">Report Number</p>
            <p className="text-base qhd:text-lg 4k:text-xl font-bold text-gray-900 dark:text-white">
              {reportNumber}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <OathStatusBadge status={status} />
            <OathButton variant="outline" size="sm" onClick={onClose}>
              Close
            </OathButton>
          </div>
        </div>

        {/* Meta grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400">Status</p>
            <p className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white mt-0.5">
              {status}
            </p>
          </div>
          <div>
            <p className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400">Submitted</p>
            <p className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white mt-0.5">
              {formatDate(submittedDate)}
            </p>
          </div>
          {reviewedBy && (
            <div>
              <p className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400">Reviewed By</p>
              <p className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white mt-0.5">
                {reviewedBy}
              </p>
            </div>
          )}
          {reviewedDate && (
            <div>
              <p className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400">Reviewed Date</p>
              <p className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white mt-0.5">
                {formatDate(reviewedDate)}
              </p>
            </div>
          )}
        </div>

        {/* Error message — only show for actual failures, not pending review info */}
        {errorMessage && status === 'Failed' && (
          <OathAlert variant="error" message={errorMessage} />
        )}

        {/* Powers list */}
        {powers.length > 0 && (
          <div>
            <p className="text-sm qhd:text-base 4k:text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Powers ({powers.length})
            </p>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Power #</th>
                      <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Defendant</th>
                      <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Proof</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {powers.map((p, idx) => (
                      <tr key={p.powerNo || idx} className="bg-white dark:bg-gray-800">
                        <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white whitespace-nowrap">{p.powerNo}</td>
                        <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300 whitespace-nowrap">{p.defendantName || '-'}</td>
                        <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300 text-right whitespace-nowrap">{formatCurrencyWithSymbol(p.bondAmount)}</td>
                        <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center whitespace-nowrap">
                          {p.isPending ? (
                            <span className="inline-flex items-center gap-1 text-xs qhd:text-sm 4k:text-base text-orange-600 dark:text-orange-400 font-medium">
                              <ClockIcon className="h-4 w-4 qhd:h-5 qhd:w-5" /> Pending
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs qhd:text-sm 4k:text-base text-green-600 dark:text-green-400 font-medium">
                              <CheckCircleIcon className="h-4 w-4 qhd:h-5 qhd:w-5" /> Discharged
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center whitespace-nowrap">
                          {!p.isPending && !p.hasProofUploaded ? (
                            <span className="text-xs qhd:text-sm 4k:text-base text-gray-400 dark:text-gray-500">&mdash;</span>
                          ) : p.hasProofUploaded ? (
                            <div className="inline-flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 text-xs qhd:text-sm 4k:text-base text-green-600 dark:text-green-400 font-medium">
                                <DocumentCheckIcon className="h-4 w-4 qhd:h-5 qhd:w-5" /> Uploaded
                              </span>
                              <OathButton variant="outline" size="sm" onClick={() => onUploadProof(p)}>View</OathButton>
                            </div>
                          ) : (
                            <div className="inline-flex items-center gap-2">
                              <OathButton variant="outline" size="sm" icon={ArrowUpTrayIcon} onClick={() => onUploadProof(p)}>Upload</OathButton>
                              <OathButton variant="outline" size="sm" icon={TrashIcon} onClick={() => onRemovePower(p.powerNo)} loading={removingPower === p.powerNo} disabled={!!removingPower} className="!text-red-600 !border-red-300 hover:!bg-red-50 dark:!text-red-400 dark:!border-red-700 dark:hover:!bg-red-900/20">Remove</OathButton>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile card layout */}
              <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
                {powers.map((p, idx) => (
                  <div key={p.powerNo || idx} className="px-3 py-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono font-bold text-sm text-gray-900 dark:text-white">{p.powerNo}</span>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300 tabular-nums">{formatCurrencyWithSymbol(p.bondAmount)}</span>
                    </div>
                    {p.defendantName && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{p.defendantName}</p>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      {p.isPending ? (
                        <span className="inline-flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-medium">
                          <ClockIcon className="h-3.5 w-3.5" /> Pending
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                          <CheckCircleIcon className="h-3.5 w-3.5" /> Discharged
                        </span>
                      )}
                      {p.hasProofUploaded ? (
                        <OathButton variant="outline" size="sm" onClick={() => onUploadProof(p)}>View Proof</OathButton>
                      ) : p.isPending ? (
                        <div className="flex items-center gap-2">
                          <OathButton variant="outline" size="sm" icon={ArrowUpTrayIcon} onClick={() => onUploadProof(p)}>Upload</OathButton>
                          <OathButton variant="outline" size="sm" icon={TrashIcon} onClick={() => onRemovePower(p.powerNo)} loading={removingPower === p.powerNo} disabled={!!removingPower} className="!text-red-600 !border-red-300 hover:!bg-red-50 dark:!text-red-400 dark:!border-red-700 dark:hover:!bg-red-900/20">Remove</OathButton>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Complete Discharge action */}
        {showCompleteAction && (
          <OathAlert variant="info">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm qhd:text-base 4k:text-lg font-medium">
                  {pendingWithProof.length} pending power{pendingWithProof.length !== 1 ? 's have' : ' has'} proof uploaded
                </p>
                <p className="text-xs qhd:text-sm 4k:text-base mt-0.5 opacity-80">
                  Submit for review to complete the discharge process.
                </p>
              </div>
              <OathButton
                size="sm"
                icon={DocumentCheckIcon}
                onClick={onCompleteDischarge}
                loading={completing}
              >
                Complete Discharge
              </OathButton>
            </div>
          </OathAlert>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

// When filtering by status tab, fetch all items so client-side pagination is accurate
const FETCH_ALL_SIZE = 500;

const DischargeHistoryTable = ({ isActive, statuses, onItemsLoaded }) => {
  // History list state
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Client-side page for status-filtered tabs
  const [clientPage, setClientPage] = useState(1);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Selected row detail state
  const [selectedReport, setSelectedReport] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Proof upload modal state
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [proofModalPower, setProofModalPower] = useState(null);

  // Complete discharge state
  const [completing, setCompleting] = useState(false);

  // Remove power state
  const [removingPower, setRemovingPower] = useState(null);

  // -------------------------------------------------------------------
  // Fetch history list
  // -------------------------------------------------------------------
  // When statuses is provided, fetch all items in one request for accurate client-side pagination
  const fetchPageSize = statuses ? FETCH_ALL_SIZE : pageSize;

  const fetchHistory = useCallback(
    async (page = 1) => {
      setLoading(true);
      setError(null);

      try {
        const response = await getHistory(statuses ? 1 : page, fetchPageSize);
        if (response.success && response.data) {
          setHistoryData(response.data);
          if (!statuses) {
            setCurrentPage(response.data.pageNumber || page);
          }
          onItemsLoaded?.(response.data.items || []);
        } else {
          setError(response.error || 'Failed to load discharge history.');
          setHistoryData(null);
        }
      } catch (err) {
        console.error('DischargeHistoryTable fetch error:', err);
        setError('An unexpected error occurred while loading history.');
        setHistoryData(null);
      } finally {
        setLoading(false);
      }
    },
    [fetchPageSize, statuses],
  );

  // Fetch when tab becomes active
  useEffect(() => {
    if (isActive) {
      fetchHistory(1);
      // Reset filters and selected detail
      setSearchTerm('');
      setStatusFilter('all');
      setClientPage(1);
      setSelectedReport(null);
      setDetail(null);
      setDetailError(null);
    }
  }, [isActive, fetchHistory]);

  // -------------------------------------------------------------------
  // Fetch detail for a selected row
  // -------------------------------------------------------------------
  const fetchDetail = useCallback(async (reportId) => {
    setDetail(null);
    setDetailLoading(true);
    setDetailError(null);

    try {
      const response = await getStatus(reportId);
      if (response.success && response.data) {
        setDetail(response.data);
      } else {
        setDetailError(response.error || 'Failed to load report details.');
      }
    } catch (err) {
      console.error('DischargeHistoryTable detail error:', err);
      setDetailError('An unexpected error occurred while loading details.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleRowClick = useCallback(
    (item) => {
      const reportId = item.reportId;
      if (!reportId) {
        toast.error('Report ID not available. History data may need to be refreshed.');
        return;
      }
      // Toggle off if clicking the same row
      if (selectedReport === reportId) {
        setSelectedReport(null);
        setDetail(null);
        setDetailError(null);
        return;
      }

      setSelectedReport(reportId);
      fetchDetail(reportId);
    },
    [selectedReport, fetchDetail],
  );

  // -------------------------------------------------------------------
  // Proof upload handlers
  // -------------------------------------------------------------------
  const handleUploadProof = useCallback((power) => {
    setProofModalPower(power);
    setProofModalOpen(true);
  }, []);

  const handleProofUpdated = useCallback(() => {
    // Re-fetch the detail to get updated proof counts
    if (selectedReport) {
      fetchDetail(selectedReport);
    }
  }, [selectedReport, fetchDetail]);

  const handleProofModalClose = useCallback(() => {
    setProofModalOpen(false);
    setProofModalPower(null);
  }, []);

  // -------------------------------------------------------------------
  // Complete discharge handler
  // -------------------------------------------------------------------
  const handleCompleteDischarge = useCallback(async () => {
    const reportId = detail?.reportId;
    if (!reportId || typeof reportId !== 'number') {
      toast.error('Report ID not available. The report detail may need to be refreshed.');
      return;
    }
    setCompleting(true);
    try {
      const response = await completeDischarge(reportId);
      if (response.success) {
        toast.success(response.data?.message || 'Discharge completion submitted successfully.');
        // Refresh the detail to show updated statuses
        fetchDetail(reportId);
        // Also refresh the history list for updated status
        fetchHistory(currentPage);
      } else {
        toast.error(response.error || 'Failed to complete discharge.');
      }
    } catch (err) {
      console.error('Complete discharge error:', err);
      toast.error('An unexpected error occurred while completing the discharge.');
    } finally {
      setCompleting(false);
    }
  }, [detail, fetchDetail, fetchHistory, currentPage]);

  // -------------------------------------------------------------------
  // Remove power from report handler
  // -------------------------------------------------------------------
  const handleRemovePower = useCallback(async (powerNo) => {
    if (!detail?.reportId) return;
    setRemovingPower(powerNo);
    try {
      const response = await removePowerFromReport(detail.reportId, powerNo);
      if (response.success) {
        toast.success(response.data?.message || `Power ${powerNo} removed from report.`);
        fetchDetail(detail.reportId);
        fetchHistory(statuses ? 1 : currentPage);
      } else {
        toast.error(response.error || 'Failed to remove power from report.');
      }
    } catch (err) {
      console.error('Remove power error:', err);
      toast.error('An unexpected error occurred while removing the power.');
    } finally {
      setRemovingPower(null);
    }
  }, [detail, fetchDetail, fetchHistory, statuses, currentPage]);

  // -------------------------------------------------------------------
  // Page change handler
  // -------------------------------------------------------------------
  const handlePageChange = useCallback(
    (page) => {
      setSelectedReport(null);
      setDetail(null);
      setDetailError(null);
      if (statuses) {
        setClientPage(page);
      } else {
        fetchHistory(page);
      }
    },
    [statuses, fetchHistory],
  );

  // Close detail panel
  const handleCloseDetail = useCallback(() => {
    setSelectedReport(null);
    setDetail(null);
    setDetailError(null);
  }, []);

  // Page size change handler
  const handlePageSizeChange = useCallback(
    (newSize) => {
      setPageSize(newSize);
      setSelectedReport(null);
      setDetail(null);
      setDetailError(null);
      // pageSize change triggers fetchHistory via its dependency
    },
    [],
  );

  // Re-fetch from page 1 when pageSize changes
  useEffect(() => {
    if (isActive && historyData) {
      fetchHistory(1);
    }
  }, [pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------
  // Derived data
  // -------------------------------------------------------------------
  const items = historyData?.items || [];
  const totalPages = historyData?.totalPages || 1;
  const totalCount = historyData?.totalCount || items.length;

  // Client-side filtering on current page
  const statusCounts = useMemo(() => {
    const counts = {
      total: items.length,
      'Completed': 0,
      'Pending Review': 0,
      'Failed': 0,
    };
    items.forEach((item) => {
      if (counts[item.status] !== undefined) counts[item.status]++;
    });
    return counts;
  }, [items]);

  // When `statuses` prop is provided, use it as a fixed filter (no user toggle)
  const effectiveStatusFilter = statuses ? null : statusFilter;

  const filteredItems = useMemo(() => {
    let result = items;

    // Fixed status filter from prop
    if (statuses) {
      result = result.filter((item) => statuses.includes(item.status));
    } else if (statusFilter !== 'all') {
      result = result.filter((item) => item.status === statusFilter);
    }

    // Search by report number
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      result = result.filter((item) =>
        item.reportNumber?.toLowerCase().includes(term),
      );
    }

    return result;
  }, [items, statuses, statusFilter, searchTerm]);

  const hasActiveFilters = (effectiveStatusFilter && effectiveStatusFilter !== 'all') || searchTerm.trim().length > 0;

  // Client-side pagination for status-filtered tabs
  const clientPageSize = DEFAULT_PAGE_SIZE;
  const clientTotalPages = statuses ? Math.max(1, Math.ceil(filteredItems.length / clientPageSize)) : totalPages;
  const clientTotalCount = statuses ? filteredItems.length : totalCount;
  // Clamp clientPage if filtered results shrink (e.g. after search)
  const effectiveClientPage = Math.min(clientPage, clientTotalPages);
  const displayItems = statuses
    ? filteredItems.slice((effectiveClientPage - 1) * clientPageSize, effectiveClientPage * clientPageSize)
    : filteredItems;

  // -------------------------------------------------------------------
  // Loading state (initial)
  // -------------------------------------------------------------------
  if (loading && !historyData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center justify-center py-16">
          <OathSpinner size="md" className="text-primary" />
          <p className="mt-3 text-sm qhd:text-base 4k:text-lg text-gray-500 dark:text-gray-400">
            Loading discharge history...
          </p>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Error state (no data to show)
  // -------------------------------------------------------------------
  if (error && !historyData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-red-400 via-red-500 to-red-400" />
        <div className="p-6 qhd:p-8 4k:p-10">
          <OathAlert variant="error" message={error} />
          <div className="mt-4 text-center">
            <OathButton variant="outline" size="sm" onClick={() => fetchHistory(1)}>
              Retry
            </OathButton>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Empty state
  // -------------------------------------------------------------------
  // Check if all items are filtered out by the statuses prop (data exists but none match)
  const noMatchingItems = statuses && items.length > 0 && filteredItems.length === 0 && !searchTerm.trim();

  if (!loading && (items.length === 0 || noMatchingItems)) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <OathEmptyState
          icon={ClockIcon}
          title={noMatchingItems ? 'No Reports' : 'No Discharge History'}
          message={noMatchingItems
            ? 'No discharge reports match this category on the current page.'
            : 'You have not submitted any discharge requests yet. Discharged powers will appear here once processed.'}
        />
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Main table render
  // -------------------------------------------------------------------
  return (
    <>
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />

        {/* Search & filter bar */}
        <div className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <OathSearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search by report #..."
            className="flex-1 w-full sm:max-w-xs"
          />

          {/* Status pills — hidden when tab-level statuses prop is used */}
          {!statuses && (
            <StatusFilterPills
              options={HISTORY_STATUS_FILTERS}
              activeFilter={statusFilter}
              onFilterChange={setStatusFilter}
              counts={statusCounts}
            />
          )}
        </div>

        {/* Inline error banner (when we have stale data + new error) */}
        {error && (
          <div className="px-5 pt-4 qhd:px-6 4k:px-8">
            <OathAlert variant="error" message={error} />
          </div>
        )}

        {/* No filter results message */}
        {hasActiveFilters && displayItems.length === 0 && items.length > 0 && (
          <OathEmptyState
            title="No matching results"
            message="No results match your filters on this page."
            action={{ label: 'Clear filters', onClick: () => { setSearchTerm(''); setStatusFilter('all'); } }}
          />
        )}

        {(!hasActiveFilters || displayItems.length > 0) && (
        <>
        {/* Desktop table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-700">
                <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Report #
                </th>
                <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-left text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Submitted Date
                </th>
                <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Total Liability
                </th>
                <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center text-xs qhd:text-sm 4k:text-base font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {displayItems.map((item) => {
                const isSelected = selectedReport === item.reportId;
                const hasDischarged = item.dischargedCount != null;

                return (
                  <React.Fragment key={item.reportId || item.reportNumber}>
                    <tr
                      onClick={() => handleRowClick(item)}
                      className={`hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors cursor-pointer ${
                        isSelected
                          ? 'bg-primary-50/50 dark:bg-primary-900/20 shadow-[inset_3px_0_0_0] shadow-primary dark:shadow-primary-light'
                          : 'bg-white dark:bg-gray-800'
                      }`}
                    >
                      <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-sm qhd:text-base 4k:text-lg font-medium text-primary dark:text-primary-light whitespace-nowrap">
                        {item.reportNumber}
                      </td>
                      <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(item.submittedDate)}
                      </td>
                      <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center whitespace-nowrap">
                        {hasDischarged ? (
                          <div className="inline-flex flex-col items-center gap-1 min-w-[7rem]">
                            <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden flex">
                              {item.dischargedCount > 0 && (
                                <div
                                  className="h-full bg-green-500 dark:bg-green-400"
                                  style={{ width: `${Math.round((item.dischargedCount / item.powerCount) * 100)}%` }}
                                />
                              )}
                              {item.pendingProofCount > 0 && (
                                <div
                                  className="h-full bg-orange-400 dark:bg-orange-500"
                                  style={{ width: `${Math.round((item.pendingProofCount / item.powerCount) * 100)}%` }}
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-2.5 text-xs qhd:text-sm 4k:text-base tabular-nums">
                              <span className="flex items-center gap-1 text-green-600 dark:text-green-400 font-semibold">
                                <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                {item.dischargedCount} discharged
                              </span>
                              {item.pendingProofCount > 0 && (
                                <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400 font-medium">
                                  <span className="h-1.5 w-1.5 rounded-full bg-orange-400" />
                                  {item.pendingProofCount} pending
                                </span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-700 dark:text-gray-300 tabular-nums">
                            {item.powerCount} power{item.powerCount !== 1 ? 's' : ''}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white text-right whitespace-nowrap">
                        {formatCurrencyWithSymbol(item.totalLiability)}
                      </td>
                      <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center whitespace-nowrap">
                        <OathStatusBadge status={item.status} />
                      </td>
                      <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
                        <ChevronRightIcon
                          className={`h-4 w-4 qhd:h-5 qhd:w-5 text-gray-400 transition-transform duration-200 ${
                            isSelected ? 'rotate-90' : ''
                          }`}
                        />
                      </td>
                    </tr>
                    {isSelected && (
                      <tr className="border-b-2 border-primary/30 dark:border-primary/20">
                        <td colSpan={6} className="p-0 shadow-[inset_3px_0_0_0] shadow-primary dark:shadow-primary-light">
                          <DetailPanel
                            detail={detail}
                            loading={detailLoading}
                            error={detailError}
                            onClose={handleCloseDetail}
                            onUploadProof={handleUploadProof}
                            onCompleteDischarge={handleCompleteDischarge}
                            completing={completing}
                            onRemovePower={handleRemovePower}
                            removingPower={removingPower}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile card layout */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-700">
          {displayItems.map((item) => {
            const isSelected = selectedReport === item.reportId;
            const hasDischarged = item.dischargedCount != null;
            return (
              <React.Fragment key={item.reportId || item.reportNumber}>
                <button
                  type="button"
                  onClick={() => handleRowClick(item)}
                  className={`w-full text-left px-3 py-3 transition-colors ${
                    isSelected
                      ? 'bg-primary-50/50 dark:bg-primary-900/20 border-l-2 border-l-primary'
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-sm font-medium text-primary dark:text-primary-light truncate">{item.reportNumber}</span>
                    <OathStatusBadge status={item.status} />
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span>{formatDate(item.submittedDate)}</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatCurrencyWithSymbol(item.totalLiability)}</span>
                  </div>
                  {hasDischarged && (
                    <div className="mt-2">
                      <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden flex">
                        {item.dischargedCount > 0 && (
                          <div className="h-full bg-green-500 dark:bg-green-400" style={{ width: `${Math.round((item.dischargedCount / item.powerCount) * 100)}%` }} />
                        )}
                        {item.pendingProofCount > 0 && (
                          <div className="h-full bg-orange-400 dark:bg-orange-500" style={{ width: `${Math.round((item.pendingProofCount / item.powerCount) * 100)}%` }} />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] tabular-nums">
                        <span className="text-green-600 dark:text-green-400 font-semibold">{item.dischargedCount} discharged</span>
                        {item.pendingProofCount > 0 && (
                          <span className="text-orange-600 dark:text-orange-400">{item.pendingProofCount} pending</span>
                        )}
                      </div>
                    </div>
                  )}
                  {!hasDischarged && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{item.powerCount} power{item.powerCount !== 1 ? 's' : ''}</p>
                  )}
                </button>
                {isSelected && (
                  <div className="border-l-2 border-l-primary bg-gray-50/50 dark:bg-gray-800/50">
                    <DetailPanel
                      detail={detail}
                      loading={detailLoading}
                      error={detailError}
                      onClose={handleCloseDetail}
                      onUploadProof={handleUploadProof}
                      onCompleteDischarge={handleCompleteDischarge}
                      completing={completing}
                      onRemovePower={handleRemovePower}
                      removingPower={removingPower}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
        </>
        )}

        {/* Loading overlay for subsequent page fetches */}
        {loading && historyData && (
          <div className="flex items-center justify-center py-4 border-t border-gray-100 dark:border-gray-700">
            <OathSpinner size="sm" className="text-primary" />
            <span className="ml-2 text-xs qhd:text-sm text-gray-500 dark:text-gray-400">Refreshing...</span>
          </div>
        )}

        {/* Pagination */}
        <OathPagination
          currentPage={statuses ? effectiveClientPage : currentPage}
          totalPages={clientTotalPages}
          totalCount={clientTotalCount}
          pageSize={statuses ? clientPageSize : pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={statuses ? undefined : handlePageSizeChange}
          isLoading={loading}
          pageSizeOptions={statuses ? undefined : [20, 50, 100]}
        />
      </div>

      {/* Proof Upload Modal */}
      {proofModalPower && (
        <ProofUploadModal
          isOpen={proofModalOpen}
          onClose={handleProofModalClose}
          power={{
            powerNo: proofModalPower.powerNo,
            defendantName: proofModalPower.defendantName,
            bondAmount: proofModalPower.bondAmount,
            proofReason: proofModalPower.proofReason,
          }}
          onProofUpdated={handleProofUpdated}
        />
      )}
    </>
  );
};

export default DischargeHistoryTable;
